import { useRef } from "react";
import { saveAs } from "file-saver";
import {
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  EllipsisIcon,
  FileTextIcon,
} from "lucide-react";

import { AudioPlayer, Column, Row, Text } from "@/components";
import { CallIcon } from "@/components/CallIcon";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { transcript } from "@/lib/transcript";
import { CallHistoryAiCall, VoiceAIOutreachStatus } from "@/types";

// Compact AI-call card interweaved into the messages timeline, mirroring
// MessageCallCard's layout with the AI gradient treatment.
export const MessageAiCallCard = ({
  call,
  candidateFirstName,
}: {
  call: CallHistoryAiCall;
  candidateFirstName: string;
}) => {
  /* React Hooks */
  const audioRef = useRef<HTMLAudioElement>(null);
  const successToast = useSuccessToast();

  /* Helpers */
  const transcriptLines = transcript.parseLines(call.transcript);
  const getRoleLabel = (role: string) =>
    role === "agent" ? "Stevie" : candidateFirstName;

  /* Render */
  const renderActionsMenu = () => {
    if (!transcriptLines?.length && !call.audioUrl) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-4 w-4 shrink-0 p-0"
            size="icon"
            type="button"
            variant="ghost"
          >
            <EllipsisIcon className="h-4 w-4 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {transcriptLines?.length ? (
            <DropdownMenuItem
              onClick={() => {
                const text = misc.buildClipboardTextForTranscript({
                  getRoleLabel,
                  transcriptLines,
                });
                void navigator.clipboard.writeText(text);
                successToast({
                  description: "Copied transcript to clipboard!",
                });
              }}
            >
              <CopyIcon className="h-4 w-4" />
              Copy full transcript
            </DropdownMenuItem>
          ) : null}
          {call.audioUrl ? (
            <DropdownMenuItem
              onClick={() => {
                if (!call.audioUrl) return;
                saveAs(
                  call.audioUrl,
                  `ai-call-${candidateFirstName}-${call.scheduledDateTime}.mp3`
                    .toLowerCase()
                    .replace(/\s+/g, "-"),
                );
              }}
            >
              <DownloadIcon className="h-4 w-4" />
              Download audio
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTranscript = () => {
    if (!transcriptLines?.length) return null;

    return (
      <>
        <CollapsibleTrigger asChild>
          <Button
            className="h-auto w-fit gap-1 p-0 text-xs font-normal hover:text-slate-700"
            type="button"
            variant="ghost"
          >
            <FileTextIcon className="h-3 w-3 shrink-0" />
            <Text className="text-xs font-normal group-data-[state=closed]/message-ai-call:inline group-data-[state=open]/message-ai-call:hidden">
              Show transcript
            </Text>
            <Text className="text-xs font-normal group-data-[state=open]/message-ai-call:inline group-data-[state=closed]/message-ai-call:hidden">
              Hide transcript
            </Text>
            <ChevronRightIcon className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]/message-ai-call:rotate-90" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-2">
            {transcriptLines.map((line, index) => (
              <Text
                className="text-sm font-normal leading-relaxed text-slate-600"
                key={`${line.time}-${String(index)}`}
              >
                {`${getRoleLabel(line.role)}: ${line.content}`}
              </Text>
            ))}
          </Column>
        </CollapsibleContent>
      </>
    );
  };

  const render = () => {
    return (
      <Collapsible className="group/message-ai-call overflow-hidden rounded-xl border border-[#CEE7FE] bg-white shadow-sm">
        <Row className="min-w-0 items-center justify-between gap-2 px-4 py-3">
          <Row className="min-w-0 items-center gap-2">
            <CallIcon
              circleClassName="h-6 w-6"
              className="h-3 w-3"
              isAi
              isVoicemail={
                call.status === VoiceAIOutreachStatus.VOICEMAIL.toString()
              }
              status={call.status}
            />
            <Text className="truncate text-sm font-medium text-slate-700">
              Stevie
            </Text>
          </Row>
          {renderActionsMenu()}
        </Row>
        <Column className="gap-2 bg-primary-400 px-4 py-3">
          {call.audioUrl ? (
            <AudioPlayer
              audioRef={audioRef}
              audioSrc={call.audioUrl}
              className="pl-0"
              trackClassName="bg-white"
            />
          ) : null}
          <Text className="text-xs font-normal">
            {dates.formatShortDateTime(call.scheduledDateTime)}
          </Text>
          {renderTranscript()}
        </Column>
      </Collapsible>
    );
  };

  return render();
};
